import { redirectTo } from '../../shared/navigation';
import { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlined from '@mui/icons-material/LockOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ROUTES } from '../../app/routes';
import { config } from '../../core/config';
import { completeLogin, discoverOrganizations } from '../../app/sessionSlice';
import { useAppDispatch } from '../../app/store';
import { PlatformApiError } from '../../core/http/errors';
import { AuthFrame, GoogleButton } from './AuthFrame';
import { countDestinations, destinationRoute, firstDestination, resolveDefault, savedDestination } from './DestinationPicker';

interface LoginLocationState {
  from?: string;
}

/**
 * Sign-in (Hub 02 §2). Step one returns organizations × destinations. The destination popup
 * (`/org-select`) is skipped when exactly one destination exists or a remembered default is still
 * valid; otherwise the user picks Configuration, a product console or My account there.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LoginLocationState;

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!login.trim() || !password) {
      setError(t('auth.login.required', 'Enter your email or username and password.'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const result = await dispatch(discoverOrganizations({ login, password })).unwrap();
      const continuation = new URLSearchParams(location.search).get('continue');
      if (continuation) {
        const allowedApi = new URL(import.meta.env.VITE_SSO_API_URL || (import.meta.env.DEV ? 'http://localhost:5100/api/v1/platform' : config.apiBaseUrl), window.location.origin);
        const target = new URL(continuation, window.location.origin);
        if (target.origin !== allowedApi.origin || target.pathname !== allowedApi.pathname.replace(/\/$/, '') + '/auth/sso/authorize') throw new Error('Invalid sign-in return address.');
        const community = result.organizations.find(o => o.code === 'COMMUNITY');
        if (!community) throw new Error('A customer account is required to use the cart.');
        await dispatch(completeLogin({ login, password, organizationId: community.id, productCode: 'PLATFORM' })).unwrap();
        redirectTo(target.href);
        return;
      }
      const organizations = result.organizations.filter((organization) => organization.destinations.length > 0);
      if (organizations.length === 0) {
        setError(t('auth.login.noOrganizations', 'This account has no active organizations. Contact an administrator.'));
        setBusy(false);
        return;
      }
      const direct = countDestinations(organizations) === 1
        ? firstDestination(organizations)
        : resolveDefault(organizations, result.defaultDestination ?? savedDestination.get(login));
      if (direct) {
        await dispatch(completeLogin({ login, password, organizationId: direct.organizationId, productCode: 'PLATFORM' })).unwrap();
        navigate(destinationRoute(direct.destination, state.from), { replace: true });
        return;
      }
      // Credentials stay in memory (history state) only until the destination is chosen.
      navigate(ROUTES.orgSelect, { state: { login, password, from: state.from, organizations, firstName: result.user?.firstName } });
    } catch (caught) {
      setError(caught instanceof PlatformApiError || caught instanceof Error ? caught.message : t('auth.login.failed', 'Sign in failed.'));
      setBusy(false);
    }
  };

  return <AuthFrame>
    <h1>{t('auth.login.title', 'Sign in to Biznesi')}</h1>
    <p className="lead">{t('auth.login.lead', 'One account for your businesses and your bookings.')}</p>

    <GoogleButton />
    <div className="ax-divider">{t('auth.or', 'or')}</div>

    {error && <div className="ax-alert error" role="alert">{error}</div>}

    <form onSubmit={handleSubmit} noValidate>
      <div className="ax-field">
        <label htmlFor="ax-login">{t('auth.login.identifier', 'Email or username')}</label>
        <div className="ax-input">
          <input id="ax-login" value={login} onChange={(event) => setLogin(event.target.value)} autoComplete="username" autoFocus required
            placeholder={t('auth.login.identifierPlaceholder', 'name@company.com')} />
        </div>
      </div>
      <div className="ax-field">
        <label htmlFor="ax-password">{t('auth.login.password', 'Password')}</label>
        <div className="ax-input">
          <input id="ax-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password" required style={{ paddingRight: 44 }} />
          <button type="button" onClick={() => setShowPassword((value) => !value)}
            aria-label={showPassword ? t('auth.login.hidePassword', 'Hide password') : t('auth.login.showPassword', 'Show password')}>
            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </button>
        </div>
      </div>
      <button type="submit" className="ax-primary" disabled={busy}>
        {busy ? t('auth.login.signingIn', 'Signing in…') : <>{t('auth.login.submit', 'Sign in')} <ArrowForwardIcon sx={{ fontSize: 18 }} /></>}
      </button>
    </form>

    <p className="ax-foot"><RouterLink to={ROUTES.forgotPassword}>Forgot password?</RouterLink></p>
    <p className="ax-foot">
      {t('auth.login.newHere', 'New here?')} <RouterLink to={ROUTES.register}>{t('auth.login.create', 'Create a free account')}</RouterLink>
    </p>
    <p className="ax-fine"><LockOutlined sx={{ fontSize: 14 }} /> {t('auth.login.secure', 'Secure session · tokens are never placed in URLs')}</p>
  </AuthFrame>;
}
